import type React from 'react';
import { Children } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketExclamationCircleIcon } from '@squareup/market-react/icons';
import errorTrackingClient from '../../../utils/error-tracking-client';
import { ProductPageHeaderSecondary } from '../index';
import { ProductPageHeader, type ProductPageHeaderProps } from '../product-page-header/ProductPageHeader';
import headerStyles from '../product-page-header/ProductPageHeader.module.scss';
import styles from './PageLayout.module.css';

export enum PageLayoutVariant {
  LandingPage = 'landingPage',
  OneColumn = 'oneColumn',
  TwoColumn = 'twoColumn',
}

const CLASS_MAP: Record<PageLayoutVariant, { container: string; content: string; footerClass: string }> = {
  [PageLayoutVariant.LandingPage]: {
    container: styles.layoutLandingPageContainer,
    content: styles.content,
    footerClass: styles.layoutLandingPageFooter,
  },
  [PageLayoutVariant.OneColumn]: {
    container: '',
    content: `${styles.layoutOneColumn} ${styles.content}`,
    footerClass: styles.layoutOneColumnFooter,
  },
  [PageLayoutVariant.TwoColumn]: {
    container: styles.layoutTwoColumnContainer,
    content: `${styles.layoutTwoColumn} ${styles.content}`,
    footerClass: styles.layoutTwoColumnFooter,
  },
};

interface PageLayoutProps extends ProductPageHeaderProps {
  readonly variant: PageLayoutVariant;
  // BANKWEB-1305: Remove after Loans pages are migrated to ProductPageHeader
  readonly header?: React.ReactNode;
  readonly showPageError?: boolean;
  readonly children: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly testId?: string;
}

export const PageLayout = ({
  variant,
  header,
  showPageError = false,
  children,
  footer,
  testId = 'page-layout',
  ...headerProps
}: PageLayoutProps) => {
  const childCount = Children.count(children);

  const isLandingPage = variant === PageLayoutVariant.LandingPage;
  const isOneColumn = variant === PageLayoutVariant.OneColumn;
  const isTwoColumn = variant === PageLayoutVariant.TwoColumn;

  if (isOneColumn) {
    if (childCount !== 1) {
      errorTrackingClient.captureException(`PageLayout OneColumn variant expects 1 child, received ${childCount}.`);
    }
  }

  if (isTwoColumn) {
    if (childCount !== 2) {
      errorTrackingClient.captureException(`PageLayout TwoColumn variant expects 2 children, received ${childCount}.`);
    }
  }

  const { container, content, footerClass } = CLASS_MAP[variant];
  const { t } = useTranslation();

  return (
    <div className={styles.layout} data-testid={testId}>
      <div className={container}>
        {header ?? (
          isOneColumn
            ? (
              <ProductPageHeaderSecondary {...headerProps} />
            )
            : (
              <ProductPageHeader
                {...headerProps}
                secondaryClassName={isLandingPage ? headerStyles.headerLandingPage : ''}
              />
            ))}
        <main className={content}>
          {showPageError
            ? (
              <div>
                <div className={styles.layoutError} data-testid={`${testId}-error`}>
                  <MarketExclamationCircleIcon
                    aria-hidden
                    // SQWP-2841: non-standard icon size (48px)
                    style={{ width: 48, height: 48, color: 'var(--core-fill-30-light-mode-color)' }}
                  />
                  <div className={`heading-20 ${styles.errorTitle}`}>
                    {t('bankingShared.error.message')}
                  </div>
                  <div className={`paragraph-30 ${styles.errorSubtext}`}>
                    {t('bankingShared.error.subtitle')}
                  </div>
                </div>
              </div>
            )
            : children}
        </main>
        {footer && (
          <footer className={`${footerClass} ${styles.footer}`}>{footer}</footer>
        )}
      </div>
    </div>
  );
};
